import { useState, useCallback } from 'react';
import { 
  KYPSearchState, 
  KYPSearchStateActions,
  PlayerData,
  PlayerProfile,
  PlayerInjury,
  PlayerTransfer,
  PlayerAchievement,
  PlayerStats,
  PlayerNameMatch,
  DetailedPlayerMatch,
  SearchJob,
  KYPPipelineType,
  StackedKYPConfig,
  IndividualArticleSummary,
  CategoryData
} from '../types/kyp-search.types';

export const useKYPSearchState = (): [KYPSearchState, KYPSearchStateActions] => {
  // Player data state
  const [playerName, setPlayerName] = useState<string>('');
  const [playerId, setPlayerId] = useState<string>('');
  const [playerData, setPlayerData] = useState<PlayerData | null>(null);
  const [playerProfile, setPlayerProfile] = useState<PlayerProfile | null>(null);
  const [playerInjuries, setPlayerInjuries] = useState<PlayerInjury[] | null>(null);
  const [playerTransfers, setPlayerTransfers] = useState<PlayerTransfer[] | null>(null);
  const [playerAchievements, setPlayerAchievements] = useState<PlayerAchievement[] | null>(null);
  const [playerStats, setPlayerStats] = useState<PlayerStats[] | null>(null);
  const [playerCareerStats, setPlayerCareerStats] = useState<Partial<PlayerStats> | null>(null);
  
  // Search results state
  const [playerMatches, setPlayerMatches] = useState<PlayerNameMatch[]>([]);
  const [detailedPlayerMatches, setDetailedPlayerMatches] = useState<DetailedPlayerMatch[]>([]);
  
  // UI state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearchingName, setIsSearchingName] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Async search state
  const [searchJob, setSearchJob] = useState<SearchJob>({
    id: null,
    status: null
  });
  const [resultsProcessed, setResultsProcessed] = useState<boolean>(false);
  
  // Automated search state
  const [isAutomatedResult, setIsAutomatedResult] = useState<boolean>(false);
  const [automatedResultTimestamp, setAutomatedResultTimestamp] = useState<string | null>(null);

  // New state for stacked KYP and category polling
  const [kypPipelineType, setKYPPipelineType] = useState<KYPPipelineType>('legacy');
  const [stackedKYPConfig, setStackedKYPConfig] = useState<StackedKYPConfig>({
    minRelevanceThreshold: 0.3,
    forceResummary: false,
  });
  const [individualSummaries, setIndividualSummaries] = useState<IndividualArticleSummary[]>([]);
  const [categoryStatuses, setCategoryStatuses] = useState<Record<string, boolean>>({});
  const [completedCategories, setCompletedCategories] = useState<CategoryData[]>([]);
  const [isPolling, setIsPolling] = useState<boolean>(false);

  // Reset methods
  const resetSearchState = useCallback(() => {
    setSearchJob({
      id: null,
      status: null
    });
    setResultsProcessed(false);
    setIsAutomatedResult(false);
    setAutomatedResultTimestamp(null);
    setCategoryStatuses({});
    setCompletedCategories([]);
    setIsPolling(false);
  }, []);

  const clearPlayerData = useCallback(() => {
    setPlayerData(null);
    setPlayerInjuries(null);
    setPlayerTransfers(null);
    setPlayerAchievements(null);
    setPlayerStats(null);
    setPlayerCareerStats(null);
    setPlayerMatches([]);
    setDetailedPlayerMatches([]);
    setError(null);
  }, []);

  const addCompletedCategories = useCallback((categories: CategoryData[]) => {
    setCompletedCategories(prev => {
      // Create a map of existing categories by name to avoid duplicates
      const existingMap = new Map(prev.map(cat => [cat.category_name, cat]));
      
      // Add or update categories from the new list
      categories.forEach(category => {
        existingMap.set(category.category_name, category);
      });
      
      // Return the merged array
      return Array.from(existingMap.values());
    });
  }, []);

  // Bundle state together
  const state: KYPSearchState = {
    playerName,
    playerId,
    playerData,
    playerProfile,
    playerInjuries,
    playerTransfers,
    playerAchievements,
    playerStats,
    playerCareerStats,
    playerMatches,
    detailedPlayerMatches,
    isLoading,
    isSearchingName,
    hasSearched,
    error,
    searchJob,
    resultsProcessed,
    isAutomatedResult,
    automatedResultTimestamp,
    kypPipelineType,
    stackedKYPConfig,
    individualSummaries,
    categoryStatuses,
    completedCategories,
    isPolling,
  };

  // Bundle actions together
  const actions: KYPSearchStateActions = {
    setPlayerName,
    setPlayerId,
    setPlayerData,
    setPlayerProfile,
    setPlayerInjuries,
    setPlayerTransfers,
    setPlayerAchievements,
    setPlayerStats,
    setPlayerCareerStats,
    setPlayerMatches,
    setDetailedPlayerMatches,
    setIsLoading,
    setIsSearchingName,
    setHasSearched,
    setError,
    setSearchJob,
    setResultsProcessed,
    setIsAutomatedResult,
    setAutomatedResultTimestamp,
    resetSearchState,
    clearPlayerData,
    setKYPPipelineType,
    setStackedKYPConfig,
    setIndividualSummaries,
    setCategoryStatuses,
    addCompletedCategories,
    setIsPolling,
  };

  return [state, actions];
};